import { useState } from "react";
import * as Sentry from "@sentry/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { Coins } from "lucide-react";
import { track } from "@/lib/analytics";
import { useTranslation } from "react-i18next";

const creditPackages = [
  {
    id: "2-credits",
    titleKey: "buyCredits.package2",
    price: "68",
    discountKey: null as string | null,
  },
  {
    id: "5-credits",
    titleKey: "buyCredits.package5",
    price: "150",
    discountKey: null as string | null,
  },
  {
    id: "10-credits",
    titleKey: "buyCredits.package10",
    price: "170",
    discountKey: "buyCredits.bestValue" as string | null,
  },
];

interface BuyCreditsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BuyCreditsModal({ open, onOpenChange }: BuyCreditsModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);

  const { mutate: purchase, isPending: loading } = useMutation({
    mutationFn: async () => {
      const pkg = creditPackages.find((p) => p.id === selectedPackage);
      if (!pkg) throw new Error("No package selected");
      track("credits_checkout_started", { package_id: pkg.id, price: pkg.price });
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/payments/create-credits-checkout-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: user!.email, packageId: pkg.id }),
        }
      );
      const data = await res.json();
      if (!data.url) throw new Error(data.error ?? "Session creation failed");
      return data.url as string;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err) => {
      toast.error(t("buyCredits.checkoutFailed"));
      Sentry.captureException(err);
    },
  });

  const handlePurchase = () => {
    if (!selectedPackage) {
      toast.error(t("buyCredits.selectPackage"));
      return;
    }
    if (!user?.email) {
      toast.error(t("buyCredits.noEmailError"));
      return;
    }
    purchase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-2">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-easly-gold/10 flex items-center justify-center">
            <Coins className="w-6 h-6 text-easly-gold" />
          </div>
          <DialogTitle className="text-center text-xl font-display">
            {t("buyCredits.title")}
          </DialogTitle>
          <DialogDescription className="text-center">{t("buyCredits.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-2">
          {creditPackages.map((pkg) => (
            <button
              key={pkg.id}
              type="button"
              onClick={() => {
                setSelectedPackage(pkg.id);
                track("credits_package_selected", { package_id: pkg.id, price: pkg.price });
              }}
              className={`w-full flex items-center justify-between rounded-lg border px-4 py-3 text-sm transition-colors ${
                selectedPackage === pkg.id
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-foreground hover:border-primary/50"
              }`}
            >
              <span className="font-semibold">{t(pkg.titleKey)}</span>
              <div className="flex items-center gap-2">
                {pkg.discountKey && (
                  <span className="text-xs text-easly-emerald font-medium">
                    {t(pkg.discountKey)}
                  </span>
                )}
                <span className="font-bold">{pkg.price} €</span>
              </div>
            </button>
          ))}
        </div>

        <Button
          className="w-full font-semibold"
          onClick={handlePurchase}
          disabled={!selectedPackage || loading}
        >
          {loading ? t("common.redirecting") : t("buyCredits.buyCreditsButton")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
